import type { ConnectionStatSummary } from "./stats";
import type { StreamStatus, SystemEvent } from "..";
import type { GpuStatsData, SystemStatsData } from "./messages";
import { MediaStreamType, SignalingStatus } from "../constants/webrtc";
import type { GamepadState } from "@onmo/gamepad-manager";

export interface WebRTCConfig {
  iceServers?: RTCIceServer[];
  offerOptions?: RTCOfferOptions;
  answerOptions?: RTCAnswerOptions;
}

export interface StreamingConnectionState {
  connectionState: RTCPeerConnectionState;
  isConnected: boolean;
  hasRemoteStream: boolean;
  error?: string;
}

export enum ConnectionErrorCode {
  SIGNALING_CONNECTION_FAILED = "ERR_SIGNALING_CONNECTION",
  CONNECTION_FAILED = "ERR_CONNECTION_FAILED",
  REPEATED_CONNECTION_FAILURE = "ERR_REPEATED_CONNECTION_FAILURE",
  TURN_FETCH_FAILED = "ERR_TURN_FETCH_FAILED",
}

export interface ConnectionError {
  media?: "audio" | "video";
  code: ConnectionErrorCode;
  message: string;
}

export interface StreamingCallbacks {
  onConnectionStateChange?: (state: StreamingConnectionState) => void;
  onStreamReceived?: (stream: MediaStream) => void;
  onStreamEnded?: () => void;
  onError?: (error: ConnectionError) => void;
  onSignalingMessage?: (message: SignalingMessage) => void;
  onChangeSignalingStatus?: (status: SignalingStatus) => void;
  onDataChannelOpen?: () => void;
  onDataChannelClose?: () => void;
  onDataChannelError?: (error: any) => void;
  onDataChannelMessage?: (data: any) => void;
  onSystemEvent?: (event: SystemEvent) => void;
  onSystemStats?: (stats: SystemStatsData) => void;
  onStatsUpdate?: (stats: ConnectionStatSummary) => void;
  onPlayStreamRequired?: (mediaStreamType: MediaStreamType) => void;
  onStartStream?: () => void;
  onStreamStatus?: (streamStatus: StreamStatus) => void;
  onLatencyMeasurement?: (latency: number) => void;
  onGpuStats?: (stats: GpuStatsData) => void;
  onLoadingPercentage?: (loadingPercentage: number) => void;
  onVirtualGamepadStateChange?: (state: GamepadState) => void;
  onPointerLocked?: () => void;
  onNetworkDisconnect?: () => void;
  onIceDisconnected?: () => void;
  onIceReconnected?: () => void;
  onNetworkReconnected?: () => void;
  onAudioResumed?: () => void;
}

export interface SignalingConfig {
  url: string;
  protocols?: string[];
  reconnectAttempts?: number;
  reconnectDelay?: number;
}

export interface SignalingMessage {
  type: "offer" | "answer" | "ice-candidate";
  data?: any;
  ice?: RTCIceCandidateInit;
  sdp?: RTCSessionDescriptionInit;
}

export interface SignalingCallbacks {
  onOpen?: () => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (error: Error) => void;
  onMessage?: (message: SignalingMessage) => void;
  onLoadingPercentage?: (loadingPercentage: number) => void;
  onChangeSignalingStatus?: (status: SignalingStatus) => void;
}

export interface MediaConstraints {
  video?: boolean | MediaTrackConstraints;
  audio?: boolean | MediaTrackConstraints;
}
